import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  Chip,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  CircularProgress
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import SearchIcon from '@mui/icons-material/Search'
import { useCorpusData } from '../../context/CorpusDataContext'

// Heat map color scale for savings values
const getHeatMapColor = (value, min, max) => {
  if (value === null || value === undefined) return 'transparent'
  const ratio = Math.min(1, Math.max(0, (value - min) / (max - min)))
  // Green gradient: lighter to darker green
  const green = Math.round(200 - ratio * 80)
  const alpha = 0.2 + ratio * 0.4
  return `rgba(76, ${green}, 80, ${alpha})`
}

// Equipment row component with expand/collapse
function EquipmentRow({ equipmentType, manufacturers, userCostKwh, userHoursOp, savingsRange }) {
  const [open, setOpen] = useState(true)
  const hasMultipleManufacturers = Object.keys(manufacturers).length > 1

  // Calculate equipment totals
  const equipmentTotals = useMemo(() => {
    let totalRecords = 0
    let totalSavings = 0
    let totalCapacity = 0
    let totalAge = 0
    let savingsCount = 0
    let capacityCount = 0
    let ageCount = 0

    Object.values(manufacturers).forEach(mfrData => {
      totalRecords += mfrData.records
      if (mfrData.avgSavings) {
        totalSavings += mfrData.avgSavings * mfrData.records
        savingsCount += mfrData.records
      }
      if (mfrData.avgCapacity) {
        totalCapacity += mfrData.avgCapacity * mfrData.records
        capacityCount += mfrData.records
      }
      if (mfrData.avgAge) {
        totalAge += mfrData.avgAge * mfrData.records
        ageCount += mfrData.records
      }
    })

    return {
      records: totalRecords,
      avgSavings: savingsCount > 0 ? totalSavings / savingsCount : null,
      avgCapacity: capacityCount > 0 ? totalCapacity / capacityCount : null,
      avgAge: ageCount > 0 ? totalAge / ageCount : null
    }
  }, [manufacturers])

  // Calculate user savings
  const calculateUserSavings = (avgSavings, avgCapacity) => {
    if (!avgSavings || !avgCapacity) return null
    // Estimate based on efficiency improvement and user parameters
    const baseKwh = avgCapacity * userHoursOp * 0.8 // 0.8 kW/ton baseline
    const savingsRatio = avgSavings / 10000 // Normalize
    return baseKwh * userCostKwh * savingsRatio * 0.2 // Rough estimate
  }

  return (
    <>
      {/* Equipment Type Header Row */}
      <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'grey.900' }}>
        <TableCell sx={{ py: 1, width: '28%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasMultipleManufacturers && (
              <IconButton size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: hasMultipleManufacturers ? 1 : 4.5 }}>
              {equipmentType}
            </Typography>
            <Chip
              label={`${equipmentTotals.records} records`}
              size="small"
              sx={{ ml: 1 }}
            />
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 600, width: '14%' }}>
          {equipmentTotals.avgSavings ? `$${Math.round(equipmentTotals.avgSavings).toLocaleString()}` : '-'}
        </TableCell>
        <TableCell align="right" sx={{ width: '10%' }}>{equipmentTotals.records}</TableCell>
        <TableCell align="right" sx={{ width: '14%' }}>
          {equipmentTotals.avgCapacity ? equipmentTotals.avgCapacity.toFixed(0) : '-'}
        </TableCell>
        <TableCell align="right" sx={{ width: '14%' }}>
          {equipmentTotals.avgAge ? equipmentTotals.avgAge.toFixed(1) : '-'}
        </TableCell>
        <TableCell
          align="right"
          sx={{
            width: '20%',
            bgcolor: getHeatMapColor(
              calculateUserSavings(equipmentTotals.avgSavings, equipmentTotals.avgCapacity),
              savingsRange.min,
              savingsRange.max
            ),
            fontWeight: 600
          }}
        >
          {calculateUserSavings(equipmentTotals.avgSavings, equipmentTotals.avgCapacity)
            ? `$${Math.round(calculateUserSavings(equipmentTotals.avgSavings, equipmentTotals.avgCapacity)).toLocaleString()}`
            : '-'}
        </TableCell>
      </TableRow>

      {/* Manufacturer Detail Rows - only show if multiple manufacturers */}
      {hasMultipleManufacturers && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0, border: 0, p: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableBody>
                  {Object.entries(manufacturers).map(([mfr, data]) => {
                    const userSavings = calculateUserSavings(data.avgSavings, data.avgCapacity)
                    return (
                      <TableRow key={mfr} hover>
                        <TableCell sx={{ pl: 6, width: '28%' }}>
                          <Typography variant="body2">{mfr}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ width: '14%' }}>
                          {data.avgSavings ? `$${Math.round(data.avgSavings).toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ width: '10%' }}>{data.records}</TableCell>
                        <TableCell align="right" sx={{ width: '14%' }}>
                          {data.avgCapacity ? data.avgCapacity.toFixed(0) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ width: '14%' }}>
                          {data.avgAge ? data.avgAge.toFixed(1) : '-'}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            width: '20%',
                            bgcolor: getHeatMapColor(userSavings, savingsRange.min, savingsRange.max)
                          }}
                        >
                          {userSavings ? `$${Math.round(userSavings).toLocaleString()}` : '-'}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function UserSavingsTable() {
  // Get case studies from corpus data context
  const { caseStudies, loading } = useCorpusData()

  // User-configurable inputs
  const [userCostKwh, setUserCostKwh] = useState(0.09)
  const [userHoursOp, setUserHoursOp] = useState(3500)

  // Filter states
  const [manufacturerSearch, setManufacturerSearch] = useState('')
  const [ageRange, setAgeRange] = useState([2, 26])
  const [capacityRange, setCapacityRange] = useState([1, 500])

  // Get unique manufacturers (excluding Unknown/null)
  const allManufacturers = useMemo(() => {
    if (!caseStudies) return []
    const mfrs = new Set()
    caseStudies.forEach(cs => {
      if (cs.manufacturer && cs.manufacturer !== 'Unknown') mfrs.add(cs.manufacturer)
    })
    return Array.from(mfrs).sort()
  }, [caseStudies])

  const [selectedManufacturers, setSelectedManufacturers] = useState(new Set(allManufacturers))

  // Group and aggregate data
  const groupedData = useMemo(() => {
    if (!caseStudies) return {}
    const groups = {}

    caseStudies.forEach(cs => {
      // Skip records without valid equipment type or manufacturer
      if (!cs.equipment_type || !cs.manufacturer || cs.manufacturer === 'Unknown') return

      // Apply filters
      if (!selectedManufacturers.has(cs.manufacturer)) return
      if (cs.equipment_age && (cs.equipment_age < ageRange[0] || cs.equipment_age > ageRange[1])) return
      if (cs.capacity_tons && (cs.capacity_tons < capacityRange[0] || cs.capacity_tons > capacityRange[1])) return

      const eqType = cs.equipment_type
      const mfr = cs.manufacturer

      if (!groups[eqType]) groups[eqType] = {}
      if (!groups[eqType][mfr]) {
        groups[eqType][mfr] = {
          records: 0,
          totalSavings: 0,
          savingsCount: 0,
          totalCapacity: 0,
          capacityCount: 0,
          totalAge: 0,
          ageCount: 0
        }
      }

      const g = groups[eqType][mfr]
      g.records++

      if (cs.annual_savings) {
        g.totalSavings += cs.annual_savings
        g.savingsCount++
      }
      if (cs.capacity_tons) {
        g.totalCapacity += cs.capacity_tons
        g.capacityCount++
      }
      if (cs.equipment_age) {
        g.totalAge += cs.equipment_age
        g.ageCount++
      }
    })

    // Calculate averages
    Object.keys(groups).forEach(eqType => {
      Object.keys(groups[eqType]).forEach(mfr => {
        const g = groups[eqType][mfr]
        g.avgSavings = g.savingsCount > 0 ? g.totalSavings / g.savingsCount : null
        g.avgCapacity = g.capacityCount > 0 ? g.totalCapacity / g.capacityCount : null
        g.avgAge = g.ageCount > 0 ? g.totalAge / g.ageCount : null
      })
    })

    return groups
  }, [caseStudies, selectedManufacturers, ageRange, capacityRange])

  // Calculate savings range for heat map
  const savingsRange = useMemo(() => {
    let min = Infinity
    let max = -Infinity

    Object.values(groupedData).forEach(mfrs => {
      Object.values(mfrs).forEach(data => {
        if (data.avgSavings) {
          const userSavings = data.avgCapacity ? data.avgCapacity * userHoursOp * 0.8 * userCostKwh * (data.avgSavings / 10000) * 0.2 : 0
          if (userSavings > 0) {
            min = Math.min(min, userSavings)
            max = Math.max(max, userSavings)
          }
        }
      })
    })

    return { min: min === Infinity ? 0 : min, max: max === -Infinity ? 1000 : max }
  }, [groupedData, userCostKwh, userHoursOp])

  // Filter manufacturers by search
  const filteredManufacturers = allManufacturers.filter(m =>
    m.toLowerCase().includes(manufacturerSearch.toLowerCase())
  )

  const toggleManufacturer = (mfr) => {
    setSelectedManufacturers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(mfr)) newSet.delete(mfr)
      else newSet.add(mfr)
      return newSet
    })
  }

  const toggleAllManufacturers = () => {
    if (selectedManufacturers.size === allManufacturers.length) {
      // All selected -> deselect all
      setSelectedManufacturers(new Set())
    } else {
      // Some or none selected -> select all
      setSelectedManufacturers(new Set(allManufacturers))
    }
  }

  const allSelected = selectedManufacturers.size === allManufacturers.length
  const someSelected = selectedManufacturers.size > 0 && selectedManufacturers.size < allManufacturers.length

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {/* Main Table Area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          User Savings - Selected Equipment
        </Typography>

        {/* User Input Fields */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            label="User Cost/kWh"
            type="number"
            size="small"
            value={userCostKwh}
            onChange={(e) => setUserCostKwh(parseFloat(e.target.value) || 0)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            sx={{ width: 150 }}
          />
          <TextField
            label="Hours of Operation/Year"
            type="number"
            size="small"
            value={userHoursOp}
            onChange={(e) => setUserHoursOp(parseInt(e.target.value) || 0)}
            sx={{ width: 180 }}
          />
        </Box>

        {/* Data Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', width: '28%' }}>
                  Equipment Type / Manufacturer
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'background.paper', width: '14%' }}>
                  Avg. Savings/Year
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'background.paper', width: '10%' }}>
                  # Records
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'background.paper', width: '14%' }}>
                  Avg. Capacity
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'background.paper', width: '14%' }}>
                  Avg. Unit Age
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    bgcolor: 'rgba(255, 235, 59, 0.3)',
                    width: '20%'
                  }}
                >
                  Avg. User Savings/Year
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(groupedData).map(([eqType, manufacturers]) => (
                <EquipmentRow
                  key={eqType}
                  equipmentType={eqType}
                  manufacturers={manufacturers}
                  userCostKwh={userCostKwh}
                  userHoursOp={userHoursOp}
                  savingsRange={savingsRange}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          User Savings calculated based on your electricity rate and operating hours.
          Green shading indicates higher savings potential.
        </Typography>
      </Box>

      {/* Filter Panel - Right Side */}
      <Paper
        elevation={0}
        sx={{
          width: 130,
          flexShrink: 0,
          p: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          alignSelf: 'flex-start'
        }}
      >
        {/* Manufacturer Filter */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Manufacturer
        </Typography>
        <TextField
          size="small"
          placeholder="Search..."
          value={manufacturerSearch}
          onChange={(e) => setManufacturerSearch(e.target.value)}
          fullWidth
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'grey.500' }} />
              </InputAdornment>
            )
          }}
        />
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleAllManufacturers}
              />
            }
            label={<Typography variant="body2" sx={{ fontWeight: 600 }}>All</Typography>}
          />
          <Divider sx={{ my: 0.5 }} />
          {filteredManufacturers.slice(0, 8).map(mfr => (
            <FormControlLabel
              key={mfr}
              control={
                <Checkbox
                  size="small"
                  checked={selectedManufacturers.has(mfr)}
                  onChange={() => toggleManufacturer(mfr)}
                />
              }
              label={<Typography variant="body2" noWrap>{mfr}</Typography>}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Age Range Slider */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Avg. Unit Age
        </Typography>
        <Box sx={{ px: 1 }}>
          <Slider
            value={ageRange}
            onChange={(e, newValue) => setAgeRange(newValue)}
            min={1}
            max={30}
            valueLabelDisplay="auto"
            size="small"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{ageRange[0]}</Typography>
            <Typography variant="caption">{ageRange[1]}</Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Capacity Range Slider */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Avg. Rated Capacity
        </Typography>
        <Box sx={{ px: 1 }}>
          <Slider
            value={capacityRange}
            onChange={(e, newValue) => setCapacityRange(newValue)}
            min={1}
            max={500}
            valueLabelDisplay="auto"
            size="small"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{capacityRange[0]}</Typography>
            <Typography variant="caption">{capacityRange[1]}</Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default UserSavingsTable
